import { am5, am5xy } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import { createLegend } from './create-legend';
import { getCategories } from './get-categories';
import { helpers } from '../../../..';
import { getColorv2 } from '../../../../helpers';
import { FONT_FAMILY, FONT_SIZE } from '../../../../theme/layout-size';

export const createSeries = ({
  root,
  chart,
  xAxis,
  yAxis,
  category,
  xAxisField,
  yAxisField,
  xAxisLabel,
  yAxisLabel,
  data,
  elementTooltipName,
  elementTooltipText,
  elementBulletName,
  weightField,
  hideLegend,
  chartSize,
  lineSeriesName,
  highlightedIndex,
  highlightedText,
}: {
  root: am5.Root;
  chart: am5xy.XYChart;
  xAxis: am5xy.ValueAxis<am5xy.AxisRenderer> | am5xy.DateAxis<am5xy.AxisRenderer>;
  yAxis: am5xy.ValueAxis<am5xy.AxisRenderer>;
  xAxisField: string;
  yAxisField: string;
  xAxisLabel: string;
  yAxisLabel: string;
  data: types.widgets.TableData;
  category: string | undefined;
  elementTooltipName: string | undefined;
  elementTooltipText: string | undefined;
  elementBulletName: string | undefined;
  lineSeriesName: string | undefined;
  weightField: string | undefined;
  hideLegend: boolean;
  chartSize: 'medium' | 'large';
  highlightedIndex: number | undefined;
  highlightedText: string | undefined;
}) => {
  const dataWithIndex = data.map((item, index) => ({
    ...item,
    __originalIndex: index,
  }));

  const categories = getCategories({ data: dataWithIndex, category, weightField, root });

  const bulletLabels: { label: am5.Label; rank: number }[] = [];
  const bulletShapes: (am5.Circle | am5.Triangle | am5.Rectangle)[] = [];

  let series: am5xy.LineSeries | am5xy.LineSeries[] = null;

  const getTooltip = (fillColor: am5.Color, isHighlighted = false, categoryValue?: { singleValue: boolean }) => {
    const baseTooltipText = elementTooltipText
      ? elementTooltipText
      : // eslint-disable-next-line max-len
        `[bold]{${elementTooltipName}}[/]\n[bold]${yAxisLabel}:[/] {valueY}\n[bold]${xAxisLabel}:[/] {valueX}${
          weightField && !categoryValue.singleValue ? `\n[bold]Weight:[/] {${weightField}}` : ''
        }`;

    const tooltipText =
      isHighlighted && highlightedText ? `[bold]${highlightedText}\n\n${baseTooltipText}` : baseTooltipText;

    const tooltip = am5.Tooltip.new(root, {
      labelText: tooltipText,
    });

    tooltip.get('background')?.setAll({ fill: fillColor });
    tooltip.label.setAll({ fontSize: FONT_SIZE.xxs, fontFamily: FONT_FAMILY });
    return tooltip;
  };

  if (category) {
    series = categories.map((categoryValue) => {
      const categoryIndex = categories.findIndex((c) => c.category === categoryValue.category);
      const fillColor = am5.color(getColorv2(categoryIndex));

      const categorySeries = chart.series.push(
        am5xy.LineSeries.new(root, {
          xAxis,
          yAxis,
          valueXField: xAxisField,
          valueYField: yAxisField,
          name: categoryValue.category as string,
          fill: fillColor,
          stroke: fillColor,
        })
      );

      categorySeries.strokes.template.setAll({ visible: false });

      categorySeries.bullets.push(function (root, series, dataItem) {
        const originalIndex = (dataItem.dataContext as any)?.__originalIndex;
        const isHighlighted = highlightedIndex !== undefined && originalIndex === highlightedIndex;

        let size = chartSize === 'large' ? 2 : 1;
        if (weightField && dataItem.dataContext) {
          const weight = Number((dataItem.dataContext as any)[weightField]);
          if (!isNaN(weight)) {
            size = categoryValue.normalizeWeight(weight);
          }
        }

        if (isHighlighted) {
          size = size * 2;
        }

        const bulletShape = categoryValue.getShape(size, false);
        bulletShape.setAll({
          fill: isHighlighted ? am5.color('#3C5688') : fillColor,
          stroke: am5.color('#ffffff'),
          strokeWidth: 0.5,
        });
        bulletShapes.push(bulletShape);

        const container = am5.Container.new(root, {
          interactive: false,
          interactiveChildren: true,
          tooltipText: '{valueY}',
          tooltip: getTooltip(fillColor, isHighlighted, categoryValue),
        });

        container.children.push(bulletShape);

        if (elementBulletName) {
          const label = am5.Label.new(root, {
            text: `{${elementBulletName}}`,
            centerX: am5.p50,
            centerY: am5.p0,
            populateText: true,
            fontSize: chartSize === 'large' ? 12 : 10,
            fontFamily: FONT_FAMILY,
            fill: root.interfaceColors.get('text'),
            dy: -25 - size * 5 - (chartSize === 'large' ? 5 : 0),
            opacity: 0,
            layer: 1,
          });

          bulletLabels.push({
            label,
            rank: categoryValue.singleValue
              ? -Infinity
              : weightField
                ? (dataItem.dataContext as any)[weightField]
                : (dataItem.dataContext as any)[yAxisField],
          });

          container.children.push(label);
        }

        return am5.Bullet.new(root, { sprite: container });
      });

      categorySeries.data.setAll(categoryValue.categoryData);

      return categorySeries;
    });
  } else {
    const lineSeries = chart.series.push(
      am5xy.LineSeries.new(root, {
        xAxis,
        yAxis,
        valueXField: xAxisField,
        valueYField: yAxisField,
        name: lineSeriesName,
      })
    );

    lineSeries.bullets.push(function (root, series, dataItem) {
      const originalIndex = (dataItem.dataContext as any)?.__originalIndex;
      const isHighlighted = highlightedIndex !== undefined && originalIndex === highlightedIndex;

      const circle = am5.Circle.new(root, {
        radius: isHighlighted ? 6 : 4,
        fill: isHighlighted ? am5.color('#3C5688') : am5.color(helpers.getColor(1)),
        stroke: am5.color('#ffffff'),
        strokeWidth: 0.5,
        tooltipText: '{valueY}',
        tooltip: getTooltip(am5.color(helpers.getColor(1)), isHighlighted),
      });

      return am5.Bullet.new(root, {
        sprite: circle,
      });
    });

    lineSeries.strokes.template.set('strokeOpacity', 0);
    lineSeries.data.setAll(dataWithIndex);

    series = [lineSeries];
  }

  if (!hideLegend) {
    createLegend({ root, chart, categories, series });
  }

  const areBoundsSeparate = (a: am5.IBounds, b: am5.IBounds) =>
    a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom;

  const overlapsWithVisible = (label: (typeof bulletLabels)[0], sorted: typeof bulletLabels) => {
    const visible = sorted.filter((l) => l.label.get('opacity') === 1);
    const bounds = label.label.globalBounds();
    return visible.some((vl) => vl !== label && !areBoundsSeparate(bounds, vl.label.globalBounds()));
  };

  const handleLabelVisibility = () => {
    const sorted = [...bulletLabels].sort((a, b) => b.rank - a.rank);
    let added = false;

    for (const label of sorted) {
      if (label.label.get('opacity') === 0 && !overlapsWithVisible(label, sorted)) {
        label.label.setAll({ opacity: 1 });
        added = true;
      }
    }

    if (added) return;

    for (let i = sorted.length - 1; i >= 0; i--) {
      const label = sorted[i];
      if (label.label.get('opacity') === 1 && overlapsWithVisible(label, sorted)) {
        label.label.setAll({ opacity: 0 });
      }
    }
  };

  root.events.once('frameended', () => {
    root.events.once('frameended', () => handleLabelVisibility());
  });

  chart.events.on('wheelended', handleLabelVisibility);

  if (chart.zoomOutButton) {
    chart.zoomOutButton.events.on('click', () => {
      setTimeout(handleLabelVisibility, 100);
    });
  }

  return series;
};
